import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import { Download, FileSpreadsheet, Loader2 } from 'lucide-react';
import { useStore } from '../store';
import { formatDateTimeInPT, getTodayInPT } from '../utils/dateUtils';
import {
  downloadCahpsExport,
  generateCahpsExport,
  listCahpsExports,
  type CahpsExportListItem,
} from '../services/cahpsExports';

const REPORTING_MONTHS = [
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

const MIN_REPORTING_YEAR = 2026;

function getReportingYears(): string[] {
  const currentYear = Number(getTodayInPT().split('-')[0]);
  const years: string[] = [];
  for (let year = currentYear; year >= MIN_REPORTING_YEAR; year--) {
    years.push(String(year));
  }
  return years;
}

function getDefaultSamplePeriod(): { month: string; year: string } {
  const [year, month] = getTodayInPT().split('-');
  return { month: String(Number(month)), year };
}

function isFuturePeriod(month: number, year: number): boolean {
  const [currentYear, currentMonth] = getTodayInPT().split('-').map(Number);
  return year > currentYear || (year === currentYear && month > currentMonth);
}

const CAHPSManager: React.FC = () => {
  const reportingYears = useMemo(() => getReportingYears(), []);
  const [selectedMonth, setSelectedMonth] = useState(
    () => getDefaultSamplePeriod().month
  );
  const [selectedYear, setSelectedYear] = useState(
    () => getDefaultSamplePeriod().year
  );
  const [exports, setExports] = useState<CahpsExportListItem[]>([]);
  const [isLoadingExports, setIsLoadingExports] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [downloadingExportId, setDownloadingExportId] = useState<string | null>(
    null
  );
  const showSuccess = useStore(state => state.showSuccess);
  const showError = useStore(state => state.showError);

  const loadExports = useCallback(async () => {
    setIsLoadingExports(true);
    try {
      setExports(await listCahpsExports());
    } catch (error) {
      console.error('Failed to load CAHPS exports:', error);
      showError('Failed to load CAHPS export history.');
    } finally {
      setIsLoadingExports(false);
    }
  }, [showError]);

  useEffect(() => {
    void loadExports();
  }, [loadExports]);

  const availableMonths = useMemo(() => {
    const [currentYear, currentMonth] = getTodayInPT().split('-').map(Number);
    const year = Number(selectedYear);
    if (year < currentYear) {
      return REPORTING_MONTHS;
    }
    return REPORTING_MONTHS.filter(
      month => Number(month.value) <= currentMonth
    );
  }, [selectedYear]);

  const handleGenerate = async () => {
    const sampleMonth = Number(selectedMonth);
    const sampleYear = Number(selectedYear);
    if (!Number.isInteger(sampleYear) || sampleYear < MIN_REPORTING_YEAR) {
      showError('Select a valid reporting year.');
      return;
    }
    if (isFuturePeriod(sampleMonth, sampleYear)) {
      showError('Exports cannot be generated for a future reporting period.');
      return;
    }

    setIsGenerating(true);
    try {
      await generateCahpsExport(sampleMonth, sampleYear);
      showSuccess('HHCAHPS export generated.');
      await loadExports();
    } catch (error) {
      console.error('Failed to generate CAHPS export:', error);
      showError('Failed to generate HHCAHPS export.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = async (item: CahpsExportListItem) => {
    setDownloadingExportId(item.id);
    try {
      await downloadCahpsExport(item.id, item.file_name);
    } catch (error) {
      console.error('Failed to download CAHPS export:', error);
      showError('Failed to download HHCAHPS export.');
    } finally {
      setDownloadingExportId(null);
    }
  };

  const formatPeriod = (month: number, year: number) => {
    const label = REPORTING_MONTHS.find(
      option => option.value === String(month)
    );
    return `${label?.label ?? month} ${year}`;
  };

  return (
    <div className='flex h-full flex-col'>
      <div className='border-b px-6 py-4'>
        <h1 className='text-lg font-semibold'>CAHPS Manager</h1>
        <p className='text-sm text-muted-foreground'>
          Generate HHCAHPS sample frame CSV exports and review previously saved
          files
        </p>
      </div>

      <div className='flex-1 space-y-6 overflow-auto px-6 py-4'>
        <Card>
          <CardHeader>
            <CardTitle>Generate Export</CardTitle>
            <CardDescription>
              Create a new HHCAHPS export using the official sample file layout
              for the selected reporting period.
            </CardDescription>
          </CardHeader>
          <CardContent className='space-y-4'>
            <div className='flex flex-wrap items-end gap-4'>
              <div className='space-y-1.5'>
                <label
                  htmlFor='cahps-reporting-month'
                  className='text-sm font-medium'
                >
                  Reporting month
                </label>
                <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                  <SelectTrigger id='cahps-reporting-month' className='w-40'>
                    <SelectValue placeholder='Select month' />
                  </SelectTrigger>
                  <SelectContent>
                    {availableMonths.map(month => (
                      <SelectItem key={month.value} value={month.value}>
                        {month.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className='space-y-1.5'>
                <label
                  htmlFor='cahps-reporting-year'
                  className='text-sm font-medium'
                >
                  Reporting year
                </label>
                <Select
                  value={selectedYear}
                  onValueChange={year => {
                    setSelectedYear(year);
                    const [currentYear, currentMonth] = getTodayInPT()
                      .split('-')
                      .map(Number);
                    if (
                      Number(year) === currentYear &&
                      Number(selectedMonth) > currentMonth
                    ) {
                      setSelectedMonth(String(currentMonth));
                    }
                  }}
                >
                  <SelectTrigger id='cahps-reporting-year' className='w-32'>
                    <SelectValue placeholder='Select year' />
                  </SelectTrigger>
                  <SelectContent>
                    {reportingYears.map(year => (
                      <SelectItem key={year} value={year}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                type='button'
                disabled={
                  isGenerating ||
                  isFuturePeriod(Number(selectedMonth), Number(selectedYear))
                }
                onClick={handleGenerate}
              >
                {isGenerating ? (
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                ) : (
                  <FileSpreadsheet className='mr-2 h-4 w-4' />
                )}
                Generate Export
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Export History</CardTitle>
            <CardDescription>
              Previously generated HHCAHPS exports are saved here so you can
              download them again later.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reporting Period</TableHead>
                  <TableHead>Patients</TableHead>
                  <TableHead>Generated</TableHead>
                  <TableHead>Generated By</TableHead>
                  <TableHead className='text-right'>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoadingExports ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className='py-10 text-center text-sm text-muted-foreground'
                    >
                      Loading saved exports...
                    </TableCell>
                  </TableRow>
                ) : exports.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className='py-10 text-center text-sm text-muted-foreground'
                    >
                      No saved exports yet. Generate an export above to see it
                      listed here.
                    </TableCell>
                  </TableRow>
                ) : (
                  exports.map(item => (
                    <TableRow key={item.id}>
                      <TableCell>
                        {formatPeriod(item.sample_month, item.sample_year)}
                      </TableCell>
                      <TableCell>{item.row_count}</TableCell>
                      <TableCell>
                        {formatDateTimeInPT(item.created_at)}
                      </TableCell>
                      <TableCell>{item.generated_by ?? '-'}</TableCell>
                      <TableCell>
                        <div className='flex justify-end'>
                          <Button
                            type='button'
                            variant='outline'
                            size='sm'
                            disabled={downloadingExportId === item.id}
                            onClick={() => void handleDownload(item)}
                          >
                            {downloadingExportId === item.id ? (
                              <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                            ) : (
                              <Download className='mr-2 h-4 w-4' />
                            )}
                            Download
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CAHPSManager;
